import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Icon, Button } from '../../components/ui';
import { CONTACT } from '../../data/nav';

const CONTACT_INFO = [
  { icon: 'mail', label: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: 'phone', label: 'Phone', value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/[^+\d]/g, '')}` },
  { icon: 'map-pin', label: 'Address', value: CONTACT.address, href: 'https://maps.google.com/?q=Ayodhya', external: true },
];

const HOURS = [
  { day: 'Monday – Friday', time: '9:00 AM – 6:00 PM' },
  { day: 'Saturday', time: '10:00 AM – 4:00 PM' },
  { day: 'Sunday', time: 'Closed' },
];

const SUBJECTS = ['Darshan & timings', 'Hotel booking', 'Puja booking', 'Travel planning', 'Partnership', 'Other'];

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'AyodhyaDham.info',
  description: 'Official digital guide and booking platform for Ayodhya pilgrimage — Ram Mandir darshan, hotels, e-puja, and travel planning.',
  url: 'https://ayodhyadham.info',
  logo: 'https://ayodhyadham.info/images/logo-small.png',
  image: 'https://ayodhyadham.info/images/design/hero-ram-mandir.webp',
  email: CONTACT.email,
  telephone: '+91-5555-123456',
  address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', postalCode: '224123', addressCountry: 'IN' },
  geo: { '@type': 'GeoCoordinates', latitude: 26.7922, longitude: 82.1998 },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '16:00' },
  ],
  sameAs: [],
};

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => { setForm((p) => ({ ...p, [k]: e.target.value })); setErrors((p) => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('Name is required');
    if (!form.email.trim()) e.email = t('Email is required');
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = t('Enter a valid email address');
    if (!form.subject.trim()) e.subject = t('Please choose a subject');
    if (!form.message.trim()) e.message = t('Message is required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    }, 1200);
  };

  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with the AyodhyaDham.info team for travel assistance, temple information and booking support."
        canonicalUrl="/contact"
        keywords="Ayodhya Dham contact, Ayodhya travel help, pilgrimage assistance"
        structuredData={localBusinessSchema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Contact Us') }]} title={t('Contact Us')} subtitle={t("We're here to help you plan a blessed journey")} />

      <div className="container content-wrap">
        <div className="content-cols" style={{ gridTemplateColumns: 'minmax(0, 1fr) 380px' }}>
          <div className="card card--pad-lg">
            <h2 className="h-section" style={{ fontSize: 24, marginBottom: 6 }}>{t('Send us a message')}</h2>
            <p className="muted" style={{ marginBottom: 22 }}>{t('Have questions about Ayodhya or need help planning your visit? We usually reply within a day.')}</p>

            {sent && (
              <div className="notice notice--success" style={{ marginBottom: 20 }}>
                <Icon name="circle-check" size={18} />
                <span>{t("Message sent! We'll get back to you soon.")}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-grid">
                <label className="field">
                  <span className="field__label">{t('Your name')}</span>
                  <span className={`control${errors.name ? ' is-invalid' : ''}`}><Icon name="user" size={16} /><input value={form.name} onChange={set('name')} placeholder="Ram Das" disabled={submitting} autoComplete="name" /></span>
                  {errors.name && <span className="field__error">{errors.name}</span>}
                </label>
                <label className="field">
                  <span className="field__label">{t('Email address')}</span>
                  <span className={`control${errors.email ? ' is-invalid' : ''}`}><Icon name="mail" size={16} /><input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" disabled={submitting} autoComplete="email" /></span>
                  {errors.email && <span className="field__error">{errors.email}</span>}
                </label>
                <label className="field span-2">
                  <span className="field__label">{t('Subject')}</span>
                  <span className={`control control--select${errors.subject ? ' is-invalid' : ''}`}><Icon name="message-circle" size={16} />
                    <select value={form.subject} onChange={set('subject')} disabled={submitting}>
                      <option value="">{t('How can we help?')}</option>
                      {SUBJECTS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                    </select>
                  </span>
                  {errors.subject && <span className="field__error">{errors.subject}</span>}
                </label>
                <label className="field span-2">
                  <span className="field__label">{t('Message')}</span>
                  <span className={`control${errors.message ? ' is-invalid' : ''}`} style={{ alignItems: 'flex-start', paddingTop: 10 }}><Icon name="send" size={16} style={{ marginTop: 10 }} /><textarea rows={5} value={form.message} onChange={set('message')} placeholder={t("Tell us what's on your mind…")} disabled={submitting} /></span>
                  {errors.message && <span className="field__error">{errors.message}</span>}
                </label>
              </div>
              <div style={{ marginTop: 22 }}>
                <Button type="submit" disabled={submitting} icon={<Icon name="send" size={16} />}>{submitting ? t('Sending…') : t('Send message')}</Button>
              </div>
            </form>
          </div>

          <aside className="content-cols__aside">
            <div className="card card--pad">
              <h3 className="h-card" style={{ marginBottom: 14 }}>{t('Reach us')}</h3>
              <ul className="info-list">
                {CONTACT_INFO.map((c) => (
                  <li key={c.label}>
                    <span className="info-list__icon"><Icon name={c.icon} size={16} /></span>
                    <div>
                      <div className="info-list__label">{t(c.label)}</div>
                      <a className="info-list__value" href={c.href} target={c.external ? '_blank' : undefined} rel={c.external ? 'noopener noreferrer' : undefined}>{c.value}</a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card card--pad">
              <h3 className="h-card" style={{ marginBottom: 14 }}>{t('Operating hours')}</h3>
              <div className="summary__rows">
                {HOURS.map((h) => (
                  <div key={h.day} className="summary__row"><span>{t(h.day)}</span><span>{t(h.time)}</span></div>
                ))}
              </div>
            </div>
            <div className="card card--pad" style={{ background: 'var(--surface-2)' }}>
              <h3 className="h-card" style={{ marginBottom: 6 }}>{t('Visiting Ayodhya?')}</h3>
              <p className="small muted" style={{ marginBottom: 12 }}>{t('Our travel guide covers how to reach, where to stay and the best time to visit.')}</p>
              <Button size="sm" variant="outline" to="/travel-guide" icon={<Icon name="arrow-right" size={14} />}>{t('Travel Guide')}</Button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
